import React, { useEffect, useRef, useState } from 'react';

import {
  customTargetChoice,
  fillRunPrompt,
  RunOptionAction,
  RunOptionChoice,
  RunOptionMode,
  RunOptionMore,
  RunOptionsPayload,
  RunOptionsSaveState,
  saveOptionCopy,
} from 'components/ExportDesign/exportDesignRunOptionsData';

// "How would you like to continue?" under a settled classification (UX slice S3; reshaped in S12 R-4,
// board 12). One question at a time, in place: 1. save, continue or start a new design; 2. what next
// (design changes to a target, licensing, a what-if, the Final Design Summary; equal choices, nothing
// suggested); 3. what will happen, anything still needed, then live or in the background. When the chat
// already asked for something, Continue names it and goes straight to step 3 (D-29). Live sends a plain
// sentence (the backend recognises a choice's sentence exactly; the others go through the router);
// "Run in the background" (S8b) saves the ticket and hands the step to the server (the chat's
// onBackground). Licensing and what-ifs run live only for now (plan §5 S12, "3b"). Save and Start a new
// design are the page's own Save Result and New Design. Only <div>s inside: the chat bubble's `p` margin
// rule outranks single classes.

export interface ExportDesignRunOptionsProps {
  runOptions: RunOptionsPayload;
  /** The latest answer, nothing running: the block can be used. Otherwise it is history. */
  active: boolean;
  /** The choice whose live (or background) sentence the user sent from this block, or -1. */
  answeredChoice: number;
  /** How that choice was run. */
  answeredMode?: RunOptionMode;
  disabled?: boolean;
  saveState: RunOptionsSaveState;
  saveDisabled?: boolean;
  onRun: (prompt: string) => void;
  /** Absent: the background option isn't shown (no team or email to tell). */
  onBackground?: (choice: RunOptionChoice) => void;
  onPrefill: (text: string) => void;
  onSave: () => void;
  onOpenSaved?: () => void;
  onNew: () => void;
}

type View = 'start' | 'next' | 'run';
type Pick = { type: 'choice'; index: number } | { type: 'more'; index: number } | { type: 'custom' };

const LIVE_LABEL = 'Walk me through it live';
const LIVE_DESCRIPTION = "Stay here and watch it work. I'll stop when it's done so you can steer.";
const OTHER_TARGET_STARTER = 'Find what would move it to ';

const choiceTitle = (choice: RunOptionChoice): string =>
  (choice.summary || choice.label).replace(/\.$/, '');

const ExportDesignRunOptions: React.FC<ExportDesignRunOptionsProps> = ({
  runOptions,
  active,
  answeredChoice,
  answeredMode = 'live',
  disabled = false,
  saveState,
  saveDisabled = false,
  onRun,
  onBackground,
  onPrefill,
  onSave,
  onOpenSaved,
  onNew,
}) => {
  const [view, setView] = useState<View>('start');
  const [pick, setPick] = useState<Pick | null>(null);
  const [value, setValue] = useState('');
  // Each step's question takes the focus, so a keyboard or screen-reader user follows the block.
  const questionRef = useRef<HTMLDivElement | null>(null);
  const moved = useRef(false);
  useEffect(() => {
    if (moved.current) questionRef.current?.focus();
    moved.current = true;
  }, [view]);

  const { choices, more } = runOptions;

  // History: folded to what was run. A block answered some other way simply goes away.
  if (!active) {
    const ran = choices[answeredChoice];
    if (!ran) return null;
    const mode = answeredMode === 'background' ? ran.background?.label || 'Run in the background' : ran.live.label;
    return (
      <section className='export-design-run-options is-answered' aria-label='How to continue'>
        <div className='export-design-ro-q'>{runOptions.question}</div>
        <div className='export-design-plan-summary'>{['Continue', choiceTitle(ran), mode].join(' › ')}</div>
      </section>
    );
  }

  const goTo = (next: Pick) => {
    setPick(next);
    setValue('');
    setView('run');
  };

  const actionOf = (id: RunOptionAction['id']): RunOptionAction | undefined =>
    runOptions.options.find((action) => action.id === id);

  // ------------------------------------------------------------------ step 1: save / continue / new
  if (view === 'start') {
    const save = saveOptionCopy(actionOf('save'), saveState);
    const opensTicket = saveState === 'saved' && Boolean(onOpenSaved);
    const go = actionOf('continue');
    const newAction = actionOf('new');
    const requested = runOptions.requested;
    return (
      <section className='export-design-run-options' aria-label='How to continue'>
        <div>
          <div className='export-design-ro-q' ref={questionRef} tabIndex={-1}>
            {runOptions.question}
          </div>
          {runOptions.sub && <div className='export-design-ro-sub'>{runOptions.sub}</div>}
        </div>
        <ol className='export-design-modes' aria-label='Next'>
          <li>
            <button
              type='button'
              className='export-design-mode'
              disabled={opensTicket ? false : disabled || saveDisabled}
              onClick={opensTicket ? onOpenSaved : onSave}
            >
              <span className='export-design-mode-label'>{save.label}</span>
              <span className='export-design-mode-desc'>{save.description}</span>
            </button>
          </li>
          <li>
            <button
              type='button'
              className='export-design-mode'
              disabled={disabled}
              onClick={() => (requested !== null ? goTo({ type: 'choice', index: requested }) : setView('next'))}
            >
              <span className='export-design-mode-label'>{go?.label || 'Continue'}</span>
              <span className='export-design-mode-desc'>
                {go?.description || "Choose what's next, then choose how to run it."}
              </span>
            </button>
          </li>
          <li>
            <button type='button' className='export-design-mode' disabled={disabled} onClick={onNew}>
              <span className='export-design-mode-label'>{newAction?.label || 'Start a new design'}</span>
              <span className='export-design-mode-desc'>
                {newAction?.description || 'Clear this chat and describe another product.'}
              </span>
            </button>
          </li>
        </ol>
      </section>
    );
  }

  // ------------------------------------------------------------------ step 2: what next
  if (view === 'next' || !pick) {
    const targets = choices
      .map((choice, index) => ({ choice: choice, index: index }))
      .filter(({ choice }) => Boolean(choice.target));
    const summaries = choices
      .map((choice, index) => ({ choice: choice, index: index }))
      .filter(({ choice }) => !choice.target);
    // "Another target…" (S12 R-14): step 3 with a box when the block offers it, else the message box.
    const otherTarget = () =>
      runOptions.customTarget ? goTo({ type: 'custom' }) : onPrefill(OTHER_TARGET_STARTER);
    const pickMore = (item: RunOptionMore, index: number) => {
      if (item.kind === 'other_target' && runOptions.customTarget) goTo({ type: 'custom' });
      else if (item.prompt || item.input) goTo({ type: 'more', index: index });
      else if (item.composer !== undefined) onPrefill(item.composer);
    };
    return (
      <section className='export-design-run-options' aria-label='How to continue'>
        <div>
          <div className='export-design-ro-trail'>
            <b>Continue</b>
          </div>
          <div className='export-design-ro-q' ref={questionRef} tabIndex={-1}>
            What would you like to do next?
          </div>
          <div className='export-design-ro-sub'>Pick one. Then choose how to run it.</div>
        </div>
        <div className='export-design-next-list'>
          {targets.length > 0 && (
            <div className='export-design-next'>
              <span className='export-design-next-label'>Design changes</span>
              <span className='export-design-next-desc'>
                Find the smallest changes that would move it to another regime, or why none can.
              </span>
              <span className='export-design-next-targets' role='group' aria-label='Move it to'>
                <span>Move it to</span>
                {targets.map(({ choice, index }) => (
                  <button
                    key={`${choice.target}-${index}`}
                    type='button'
                    className='export-design-target-choice'
                    disabled={disabled}
                    onClick={() => goTo({ type: 'choice', index: index })}
                  >
                    {choice.label}
                  </button>
                ))}
                <button
                  type='button'
                  className='export-design-link-btn'
                  disabled={disabled}
                  onClick={otherTarget}
                >
                  Another target…
                </button>
              </span>
            </div>
          )}
          {more.map((item, index) =>
            targets.length > 0 && item.kind === 'other_target' ? null : (
              <button
                key={`${item.label}-${index}`}
                type='button'
                className='export-design-next'
                disabled={disabled}
                onClick={() => pickMore(item, index)}
              >
                <span className='export-design-next-label'>{item.label}</span>
                {item.description && <span className='export-design-next-desc'>{item.description}</span>}
              </button>
            ),
          )}
          {summaries.map(({ index }) => (
            <button
              key={`summary-${index}`}
              type='button'
              className='export-design-next'
              disabled={disabled}
              onClick={() => goTo({ type: 'choice', index: index })}
            >
              <span className='export-design-next-label'>Final Design Summary</span>
              <span className='export-design-next-desc'>
                One report with the classification, the specs behind it and anything worked out so far.
              </span>
            </button>
          ))}
        </div>
        <div className='export-design-ro-back'>
          <button type='button' className='export-design-link-btn' disabled={disabled} onClick={() => setView('start')}>
            Back
          </button>
        </div>
      </section>
    );
  }

  // ------------------------------------------------------------------ step 3: what will happen, how to run it
  const custom = pick.type === 'custom' ? runOptions.customTarget : undefined;
  // A typed target becomes a choice like a target button once there is something in the box.
  const typedChoice = custom && value.trim() ? customTargetChoice(custom, value) : undefined;
  const choice = pick.type === 'choice' ? choices[pick.index] : typedChoice;
  const item = pick.type === 'more' ? more[pick.index] : undefined;
  const title = custom ? custom.title : choice ? choiceTitle(choice) : item?.title || item?.label || '';
  const input = custom ? custom.input : item?.input;
  const needsValue = Boolean(input) && !value.trim();
  const livePrompt = choice
    ? choice.live.prompt
    : item?.prompt || (input ? fillRunPrompt(input.prompt_template, value) : '');
  const runLive = () => {
    if (disabled || needsValue || !livePrompt) return;
    onRun(livePrompt);
  };
  const background = custom ? custom.background : choice?.background;
  const canBackground = Boolean(background && onBackground);
  const typical = custom?.typical || choice?.typical || item?.typical;
  const foot = [typical, item ? 'This one runs here in the chat.' : 'Nothing runs until you pick an option below.']
    .filter(Boolean)
    .join(' ');
  const fieldId = `export-design-need-${pick.type === 'custom' ? 'target' : pick.index}`;
  const planSteps = custom ? custom.steps : item?.steps;

  return (
    <section className='export-design-run-options' aria-label='How to continue'>
      <div>
        <div className='export-design-ro-trail'>
          <b>Continue</b> › <b>{title}</b>
        </div>
        <div className='export-design-ro-q' ref={questionRef} tabIndex={-1}>
          How should I run it?
        </div>
      </div>

      <div className='export-design-plan'>
        <div className='export-design-plan-h'>What will happen</div>
        {choice && !custom && (
          <ol className='export-design-plan-steps'>
            {choice.steps.map((step) => (
              <li key={step.id} className={step.done ? 'is-done' : undefined}>
                {step.text}
                {step.done && <span className='export-design-step-done'>Done</span>}
                {step.note && <div className='export-design-step-note'>{step.note}</div>}
              </li>
            ))}
          </ol>
        )}
        {planSteps && planSteps.length > 0 && (
          <ol className='export-design-plan-steps'>
            {planSteps.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ol>
        )}
        {input && (
          <div className='export-design-need'>
            <label htmlFor={fieldId}>{input.label || 'Needed before it can run'}</label>
            <input
              id={fieldId}
              placeholder={input.placeholder}
              value={value}
              disabled={disabled}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') runLive();
              }}
            />
            {input.help && <span className='export-design-need-help'>{input.help}</span>}
          </div>
        )}
        <div className='export-design-plan-foot'>{foot}</div>
      </div>

      <ol className='export-design-modes' aria-label='How to run it'>
        <li>
          <button type='button' className='export-design-mode' disabled={disabled || needsValue} onClick={runLive}>
            <span className='export-design-mode-label'>{choice?.live.label || custom?.live.label || LIVE_LABEL}</span>
            <span className='export-design-mode-desc'>
              {choice?.live.description || custom?.live.description || LIVE_DESCRIPTION}
            </span>
          </button>
        </li>
        {canBackground && background && (
          <li>
            <button
              type='button'
              className='export-design-mode'
              disabled={disabled || needsValue}
              onClick={() => onBackground && choice && onBackground(choice)}
            >
              <span className='export-design-mode-label'>{background.label}</span>
              {background.description && <span className='export-design-mode-desc'>{background.description}</span>}
            </button>
          </li>
        )}
      </ol>

      <div className='export-design-ro-back'>
        <button type='button' className='export-design-link-btn' disabled={disabled} onClick={() => setView('next')}>
          Change what to run
        </button>
      </div>
    </section>
  );
};

export default ExportDesignRunOptions;
